/**
 * Connect Auth Provider
 * Main auth orchestrator that implements the full flow from the doc:
 *
 * Normal flow:
 * 1. validateUserSession() — GET /api/v0/users/details
 * 2. getProviderUsers() — GET /api/v0/auth/providers/users
 * 3. getProviderDetails(providerId) — GET /api/v0/auth/providers/{id}
 *
 * Agent flow (when ?provider_id= is in URL):
 * 1. validateUserSession() — GET /api/v0/users/details
 * 2. validateAgentRole() — GET /leads/agents/current
 * 3. getProviderDetails(providerId) — GET /api/v0/auth/providers/{id}
 */

import { identifyPostHogUser, posthogEventEmitter } from "@/lib/posthog";
import {
	getProviderDetails,
	getProviderUsers,
	validateAgentRole,
	validateUserSession,
} from "./api";
import { LOGIN_REDIRECT_URL } from "./constants";
import type { ConnectProviderContext } from "./types";

export interface AuthResult {
	success: boolean;
	context?: ConnectProviderContext;
	redirectTo?: string;
}

/**
 * Run the full connect auth flow
 * @param providerIdFromUrl - provider_id from URL query (agent access)
 */
export async function authenticateConnectUser(
	providerIdFromUrl?: string | null,
): Promise<AuthResult> {
	try {
		// Step 1: Validate user session
		console.log("[Connect Auth] Step 1: Validating user session...");
		const user = await validateUserSession();
		console.log("[Connect Auth] Step 1 SUCCESS: User =", user.email);

		let providerId: number;
		let providerUserId: number | string = "";
		let providerUserName = "";
		let showProductTour = false;
		const isAgent = !!providerIdFromUrl;

		if (providerIdFromUrl) {
			// AGENT ACCESS FLOW
			console.log(
				"[Connect Auth] Agent flow: provider_id =",
				providerIdFromUrl,
			);
			try {
				await validateAgentRole();
				console.log("[Connect Auth] Step 2 SUCCESS: Agent validated");
			} catch (err) {
				console.error("[Connect Auth] Step 2 FAILED: Not an agent", err);
				return {
					success: false,
					redirectTo: `${LOGIN_REDIRECT_URL}?login=true&next=${window.location.pathname}${window.location.search}`,
				};
			}
			providerId = Number(providerIdFromUrl);
		} else {
			// NORMAL PROVIDER FLOW
			console.log("[Connect Auth] Step 2: Checking provider assignment...");
			const providerUsersResponse = await getProviderUsers();
			console.log(
				"[Connect Auth] Step 2 response:",
				JSON.stringify(providerUsersResponse).slice(0, 200),
			);

			if (
				!providerUsersResponse.data?.result?.length ||
				providerUsersResponse.message === "user-not-assigned-to-any-provider"
			) {
				console.error("[Connect Auth] Step 2 FAILED: Not a provider");
				return { success: false, redirectTo: LOGIN_REDIRECT_URL };
			}

			const providerUser = providerUsersResponse.data.result[0];
			providerId = providerUser.provider_id;
			providerUserId = providerUser.id;
			providerUserName = providerUser.user_details?.name || "";
			showProductTour = providerUser.meta?.show_product_tour || false;
			console.log("[Connect Auth] Step 2 SUCCESS: provider_id =", providerId);
		}

		// Step 3: Get provider details
		console.log(
			"[Connect Auth] Step 3: Fetching provider details for id =",
			providerId,
		);
		let providerName = "";
		let email = "";
		let navTabs: string[] = [];
		let providerDetails = null;

		try {
			const detailsResponse = await getProviderDetails(providerId);
			providerDetails = detailsResponse.data;
			providerName = providerDetails.name || "";
			email = providerDetails.data?.emails?.[0] || "";
			navTabs = providerDetails.navbar_config || [];
			console.log(
				"[Connect Auth] Step 3 SUCCESS: provider =",
				providerName,
				"navTabs =",
				navTabs,
			);
		} catch (err) {
			console.warn("[Connect Auth] Step 3 FAILED (continuing):", err);
		}

		const context: ConnectProviderContext = {
			providerId,
			providerName,
			email,
			navTabs,
			isAgent,
			userData: {
				providerUserId,
				name: providerUserName,
				showProductTour,
			},
			providerDetails,
		};

		console.log("[Connect Auth] AUTH SUCCESS — context:", {
			providerId,
			providerName,
			isAgent,
			navTabs,
		});

		// Identify user in PostHog and fire login event
		const userEmail = email || user.email;
		identifyPostHogUser(userEmail, {
			name: providerUserName || user.name,
			provider_id: providerId,
			provider_name: providerName,
			is_agent: isAgent,
			funnel: "connect_login",
		});

		posthogEventEmitter({
			action: "supply_dashboard_login",
			category: "supply_platform",
			label: isAgent ? "agent_access" : "provider_access",
			provider_name: providerName,
			provider_id: providerId,
			email: userEmail,
		});

		return { success: true, context };
	} catch (error) {
		console.error("[Connect Auth] AUTH FAILED — error:", error);
		return { success: false, redirectTo: LOGIN_REDIRECT_URL };
	}
}
